import { useState } from 'react';
import { Link } from 'react-router-dom';

function ContactModal({ onClose }: { onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7pj49el0bai2p3ha1b0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-amber-600"></i>
            </div>
            <h3 className="text-xl font-light text-gray-900 mb-2">Message Sent</h3>
            <p className="text-sm text-gray-500 font-light">
              A member of our partner team will be in touch within 24 hours.
            </p>
            <button
              onClick={onClose}
              className="mt-6 px-6 py-2.5 bg-[#1a2b3c] text-white text-sm font-light rounded-full hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-light text-gray-900 mb-1">
              Contact Us
            </h3>
            <p className="text-sm text-gray-500 font-light mb-6">
              Share a few details and we&apos;ll be in touch within 24 hours.
            </p>
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                    placeholder="Smith"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">Email</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                  placeholder="(321) 000-0000"
                />
              </div>
              <div>
                <label className="block text-xs font-light text-gray-500 uppercase tracking-widest mb-1.5">Property Name</label>
                <input
                  type="text"
                  name="property_name"
                  required
                  className="w-full px-4 py-2.5 text-sm font-light border border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                  placeholder="Hotel / Property Name"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-[#1a2b3c] text-white text-sm font-light rounded-full hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {submitting ? 'Submitting...' : 'Get Started'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function PerformanceHero() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Luxury%20Florida%20hotel%20aerial%20view%20at%20golden%20hour%2C%20premium%20resort%20property%20with%20pool%20and%20palm%20trees%2C%20warm%20amber%20sunset%20light%20casting%20long%20shadows%20across%20manicured%20grounds%2C%20sophisticated%20hospitality%20architecture%2C%20high-end%20hotel%20exterior%20photography%2C%20rich%20warm%20tones%2C%20cinematic%20composition&width=1600&height=900&seq=perf-hero-001&orientation=landscape"
            alt="Florida Hotel Performance"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 mb-8">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
              <span className="text-xs font-light text-amber-400 uppercase tracking-widest" style={{ letterSpacing: '0.25em' }}>
                Florida's First Performance-Guaranteed Hotel Management Partner
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl lg:text-7xl font-extralight text-white leading-tight mb-6">
              We Don't Just Manage<br />
              <span className="text-amber-400 italic">Your Hotel.</span>
            </h1>
            <h2 className="text-4xl lg:text-5xl font-extralight text-white mb-8">
              We Guarantee the Results.
            </h2>

            {/* Subheadline */}
            <p className="text-base lg:text-lg font-light text-white/75 leading-relaxed mb-8 max-w-2xl">
              360 Hospitality Group partners with independent hotel owners, investors, and operators across Florida to drive measurable RevPAR growth, reduce operational costs, and unlock the full value of your asset — with a performance-based model that puts our fees on the line alongside yours.
            </p>

            {/* Services Overview — First Fold Keywords */}
            <div className="flex flex-wrap items-center gap-3 mb-10">
              {[
                { icon: 'ri-hotel-line', label: 'Hotel Management Service' },
                { icon: 'ri-line-chart-line', label: 'Hotel Revenue Management Service' },
                { icon: 'ri-compass-3-line', label: 'Hotel Brand Management Service' },
              ].map((svc, i) => (
                <div
                  key={i}
                  className="group inline-flex items-center space-x-2 px-4 py-2.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-amber-400/40 transition-all cursor-default"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${svc.icon} text-sm text-amber-400`}></i>
                  </div>
                  <span className="text-xs font-light text-white/90 whitespace-nowrap">{svc.label}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-start gap-3">
              <button
                onClick={() => setModalOpen(true)}
                className="group inline-flex items-center space-x-2 px-6 py-3 bg-[#1a2b3c] text-white text-sm font-light rounded-full hover:bg-[#243447] border border-amber-400/40 hover:border-amber-400/70 transition-all cursor-pointer whitespace-nowrap"
              >
                <span>Contact Us</span>
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2 animate-bounce">
          <div className="w-px h-8 bg-white/30"></div>
          <i className="ri-arrow-down-line text-white/40 text-sm"></i>
        </div>
      </section>

      {modalOpen && <ContactModal onClose={() => setModalOpen(false)} />}
    </>
  );
}
